import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ArrowDownTrayIcon,
  SparklesIcon,
  FunnelIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  InformationCircleIcon,
  ChevronDownIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { hmdaApi, type EditSortKey } from '../lib/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';

type EditFilter = {
  editType?: string;
  severity?: string;
  editCode?: string;
};

type TabId = 'severity' | 'by-loan' | 'by-code';

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
}

export default function HmdaReportPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabId>('severity');
  const [editPage, setEditPage] = useState(1);
  const [filter, setFilter] = useState<EditFilter>({});
  const [editCodeInput, setEditCodeInput] = useState('');
  const debouncedEditCode = useDebounce(editCodeInput, 400);
  const [showFilters, setShowFilters] = useState(false);
  const [verifyingEditId, setVerifyingEditId] = useState<string | null>(null);
  const [verifyNote, setVerifyNote] = useState('');
  const [exporting, setExporting] = useState<string | null>(null);
  const [expandedLoan, setExpandedLoan] = useState<number | null>(null);
  const [expandedCode, setExpandedCode] = useState<string | null>(null);
  const editLimit = 50;

  // One sort for every edit table on the page, applied by the SERVER. The lists
  // are paginated (and the drill-downs capped at 200), so sorting in the browser
  // would reorder one page and look complete while being wrong.
  const [sort, setSort] = useState<{ sortBy: EditSortKey; sortDir: 'asc' | 'desc' }>({
    sortBy: 'rowNumber',
    sortDir: 'asc',
  });
  const onSort = (key: EditSortKey) => {
    setSort(s => ({ sortBy: key, sortDir: s.sortBy === key && s.sortDir === 'asc' ? 'desc' : 'asc' }));
    setEditPage(1); // page 3 of a different ordering is a meaningless place to land
  };

  // Sync debounced edit code into filter
  useEffect(() => {
    setFilter(f => ({ ...f, editCode: debouncedEditCode || undefined }));
    setEditPage(1);
  }, [debouncedEditCode]);

  // Fetch validation summary
  const { data: validationData, isLoading, error: validationError } = useQuery({
    queryKey: ['hmda-validation', id],
    queryFn: async () => {
      const response = await hmdaApi.getValidation(id!);
      return response.data;
    },
    enabled: !!id,
  });

  // Fetch edits (paginated + filtered) — used by "By Severity" tab
  const { data: editsData, isLoading: editsLoading } = useQuery({
    queryKey: ['hmda-edits', id, editPage, filter, sort],
    queryFn: async () => {
      const response = await hmdaApi.getEdits(id!, {
        ...filter,
        ...sort,
        page: editPage,
        limit: editLimit,
      });
      return response.data;
    },
    enabled: !!id && activeTab === 'severity',
  });

  // Fetch by-loan grouping
  const { data: byLoanData, isLoading: byLoanLoading } = useQuery({
    queryKey: ['hmda-edits-by-loan', id],
    queryFn: async () => {
      const response = await hmdaApi.getEditsByLoan(id!);
      return response.data;
    },
    enabled: !!id && activeTab === 'by-loan',
  });

  // Fetch by-code grouping
  const { data: byCodeData, isLoading: byCodeLoading } = useQuery({
    queryKey: ['hmda-edits-by-code', id],
    queryFn: async () => {
      const response = await hmdaApi.getEditsByCode(id!);
      return response.data;
    },
    enabled: !!id && activeTab === 'by-code',
  });

  // Fetch progress (verified warnings)
  const { data: progressData } = useQuery({
    queryKey: ['hmda-progress', id],
    queryFn: async () => {
      const response = await hmdaApi.getProgress(id!);
      return response.data;
    },
    enabled: !!id,
  });

  // Drill-down edits for expanded loan row
  const { data: loanDrillData, isLoading: loanDrillLoading } = useQuery({
    queryKey: ['hmda-edits-drill-loan', id, expandedLoan, sort],
    queryFn: async () => {
      const response = await hmdaApi.getEdits(id!, {
        rowNumber: expandedLoan!,
        limit: 200,
        ...sort,
      });
      return response.data;
    },
    enabled: !!id && expandedLoan !== null,
  });

  // Drill-down edits for expanded edit code
  const { data: codeDrillData, isLoading: codeDrillLoading } = useQuery({
    queryKey: ['hmda-edits-drill-code', id, expandedCode, sort],
    queryFn: async () => {
      const response = await hmdaApi.getEdits(id!, {
        editCode: expandedCode!,
        limit: 200,
        ...sort,
      });
      return response.data;
    },
    enabled: !!id && expandedCode !== null,
  });

  // AI explanations — cached per validation
  const {
    data: explanationsData,
    isLoading: explanationsLoading,
    refetch: fetchExplanations,
  } = useQuery({
    queryKey: ['hmda-explanations', id],
    queryFn: async () => {
      const response = await hmdaApi.explain(id!);
      return response.data;
    },
    enabled: false,
    staleTime: Infinity,
  });

  const handleExplain = async () => {
    const result = await fetchExplanations();
    if (result.error) {
      toast.error((result.error as any)?.response?.data?.error || 'Failed to generate explanations');
    } else {
      toast.success('Explanations generated');
    }
  };

  // Verify warning
  const verifyMutation = useMutation({
    mutationFn: ({ editId, note }: { editId: string; note?: string }) =>
      hmdaApi.verifyEdit(id!, editId, note),
    onSuccess: () => {
      toast.success('Warning verified');
      setVerifyingEditId(null);
      setVerifyNote('');
      queryClient.invalidateQueries({ queryKey: ['hmda-edits', id] });
      queryClient.invalidateQueries({ queryKey: ['hmda-edits-drill-loan', id] });
      queryClient.invalidateQueries({ queryKey: ['hmda-edits-drill-code', id] });
      queryClient.invalidateQueries({ queryKey: ['hmda-edits-by-loan', id] });
      queryClient.invalidateQueries({ queryKey: ['hmda-edits-by-code', id] });
      queryClient.invalidateQueries({ queryKey: ['hmda-progress', id] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to verify');
    },
  });

  // Delete validation
  const deleteMutation = useMutation({
    mutationFn: () => hmdaApi.deleteValidation(id!),
    onSuccess: () => {
      toast.success('Validation deleted');
      queryClient.invalidateQueries({ queryKey: ['hmda-validations'] });
      navigate('/hmda/validations');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete');
    },
  });

  const handleDelete = () => {
    if (window.confirm('Delete this validation and all its edit results? This cannot be undone.')) {
      deleteMutation.mutate();
    }
  };

  // Export handlers
  const downloadBlob = useCallback((blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  }, []);

  const handleExportCsv = async (type: 'detail' | 'summary') => {
    if (exporting) return;
    setExporting(type === 'summary' ? 'summary-csv' : 'csv');
    try {
      const response = await hmdaApi.exportCsv(id!, type);
      downloadBlob(response.data, `hmda-${type}-${Date.now()}.csv`);
      toast.success(`${type === 'summary' ? 'Summary' : 'Detail'} CSV downloaded`);
    } catch {
      toast.error('CSV export failed');
    } finally {
      setExporting(null);
    }
  };

  const handleExportPdf = async () => {
    if (exporting) return;
    setExporting('pdf');
    try {
      const response = await hmdaApi.exportPdf(id!);
      downloadBlob(response.data, `hmda-report-${Date.now()}.pdf`);
      toast.success('PDF report downloaded');
    } catch {
      toast.error('PDF export failed');
    } finally {
      setExporting(null);
    }
  };

  const handleExportCorrected = async () => {
    if (exporting) return;
    setExporting('corrected');
    try {
      const response = await hmdaApi.exportCorrected(id!);
      downloadBlob(response.data, `hmda-corrected-${Date.now()}.txt`);
      toast.success('Corrected file downloaded');
    } catch {
      toast.error('Corrected file export failed');
    } finally {
      setExporting(null);
    }
  };

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Loading validation report...</div>;
  }

  if (validationError || !validationData) {
    return (
      <div className="text-center py-12">
        <XCircleIcon className="mx-auto h-12 w-12 text-gray-300" />
        <p className="mt-4 text-gray-500">
          {(validationError as any)?.response?.status === 404
            ? 'Validation not found.'
            : 'Failed to load validation report.'}
        </p>
        <Link to="/hmda/validations" className="mt-4 inline-block text-ocean-600 hover:text-ocean-700 text-sm font-medium">
          Back to validations
        </Link>
      </div>
    );
  }

  const { validation, summary } = validationData;
  const canSubmit = summary.syntacticalErrors === 0 && summary.validityErrors === 0;
  const edits = editsData?.edits || [];
  const editPagination = editsData?.pagination;
  const editTotalPages = editPagination?.totalPages || 1;

  const tabs: { id: TabId; label: string; count?: number }[] = [
    { id: 'severity', label: 'By Severity', count: summary.totalIssues },
    { id: 'by-loan', label: 'By Loan', count: summary.affectedLoans },
    { id: 'by-code', label: 'By Edit Code' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/hmda/validations"
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to validations
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{validation.fileName}</h1>
            <p className="text-sm text-gray-500 mt-1">
              {validation.institutionName} &middot; LEI: {validation.lei} &middot;{' '}
              {validation.calendarYear} &middot; {validation.totalLoans?.toLocaleString()} loans
            </p>
            {validation.fileHash && (
              <p className="text-xs text-gray-400 mt-0.5 font-mono" title="SHA-256 file hash">
                SHA-256: {validation.fileHash.slice(0, 16)}...
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleExportCsv('detail')}
              disabled={!!exporting}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting === 'csv' ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <ArrowDownTrayIcon className="h-4 w-4" />}
              CSV
            </button>
            <button
              onClick={() => handleExportCsv('summary')}
              disabled={!!exporting}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting === 'summary-csv' ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <ArrowDownTrayIcon className="h-4 w-4" />}
              Summary CSV
            </button>
            <button
              onClick={handleExportPdf}
              disabled={!!exporting}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting === 'pdf' ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <DocumentTextIcon className="h-4 w-4" />}
              PDF
            </button>
            {summary.totalIssues > 0 && (
              <button
                onClick={handleExportCorrected}
                disabled={!!exporting}
                className="inline-flex items-center gap-1.5 rounded-lg border border-ocean-300 bg-ocean-50 px-3 py-2 text-sm font-medium text-ocean-700 hover:bg-ocean-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exporting === 'corrected' ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <ArrowDownTrayIcon className="h-4 w-4" />}
                Corrections
              </button>
            )}
            <button
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Delete validation"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Submission readiness banner */}
      <div className={clsx(
        'rounded-lg p-4 mb-6 border',
        canSubmit
          ? 'bg-green-50 border-green-200'
          : 'bg-red-50 border-red-200'
      )}>
        <div className="flex items-center gap-3">
          {canSubmit ? (
            <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
          ) : (
            <XCircleIcon className="h-6 w-6 text-red-600 flex-shrink-0" />
          )}
          <div>
            <p className={clsx('font-semibold', canSubmit ? 'text-green-800' : 'text-red-800')}>
              {canSubmit
                ? 'Ready for CFPB Submission'
                : 'Not Ready \u2014 Errors Must Be Fixed'}
            </p>
            <p className={clsx('text-sm mt-0.5', canSubmit ? 'text-green-700' : 'text-red-700')}>
              {canSubmit
                ? 'No blocking syntactical or validity errors. Quality warnings should be reviewed but do not block submission.'
                : `${summary.syntacticalErrors} syntactical error${summary.syntacticalErrors !== 1 ? 's' : ''} and ${summary.validityErrors} validity error${summary.validityErrors !== 1 ? 's' : ''} must be corrected before the CFPB will accept this file.`}
            </p>
          </div>
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <StatCard
          label="Syntactical Errors"
          value={summary.syntacticalErrors}
          color={summary.syntacticalErrors > 0 ? 'red' : 'green'}
          icon={XCircleIcon}
        />
        <StatCard
          label="Validity Errors"
          value={summary.validityErrors}
          color={summary.validityErrors > 0 ? 'red' : 'green'}
          icon={XCircleIcon}
        />
        <StatCard
          label="Quality Warnings"
          value={summary.qualityWarnings}
          color={summary.qualityWarnings > 0 ? 'amber' : 'green'}
          icon={ExclamationTriangleIcon}
        />
        <StatCard
          label="Macro Warnings"
          value={summary.macroWarnings}
          color={summary.macroWarnings > 0 ? 'amber' : 'green'}
          icon={ExclamationTriangleIcon}
        />
      </div>

      {/*
        Clarid findings sit OUTSIDE the four stat cards on purpose. That row answers "can I submit
        this file?" — these don't bear on it. They are our own fair lending observations, the bank
        filed correctly, and nothing here blocks a submission. Saying so plainly is the point:
        a compliance officer who reads them as CFPB defects will go hunting for a problem that
        isn't there.
      */}
      {!!summary.claridFindings && (
        <div className="mb-6 rounded-lg border border-teal-200 bg-teal-50 p-4">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex rounded-full bg-teal-100 px-2 py-0.5 text-xs font-medium text-teal-700">
              Clarid
            </span>
            <div>
              <p className="text-sm font-medium text-teal-900">
                {summary.claridFindings} fair lending{' '}
                {summary.claridFindings === 1 ? 'observation' : 'observations'} in this file
              </p>
              <p className="mt-0.5 text-sm text-teal-800">
                These are Clarid's own checks, not CFPB edits. They do not affect whether this file
                can be submitted, and they are not defects to correct — an application is reported
                as the applicant self-identified it. Filter the table below by{' '}
                <span className="font-medium">Clarid</span> to read them.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs font-medium text-gray-500 uppercase">Total Issues</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{summary.totalIssues}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs font-medium text-gray-500 uppercase">Clean Loans</p>
          <p className="text-2xl font-bold text-green-600 mt-1">
            {summary.cleanLoans} <span className="text-sm font-normal text-gray-500">of {validation.totalLoans}</span>
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs font-medium text-gray-500 uppercase">Affected Loans</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{summary.affectedLoans}</p>
        </div>
      </div>

      {/* Progress bar — verified warnings */}
      {progressData && progressData.totalWarnings > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-700">Warning Review Progress</p>
            <p className="text-sm text-gray-500">
              {progressData.verified} of {progressData.totalWarnings} verified
            </p>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className={clsx(
                'h-3 rounded-full transition-all duration-300',
                progressData.percent === 100 ? 'bg-green-500' : 'bg-ocean-500'
              )}
              style={{ width: `${progressData.percent}%` }}
            />
          </div>
          {progressData.percent === 100 && (
            <p className="text-xs text-green-600 font-medium mt-1.5">
              All warnings reviewed and verified
            </p>
          )}
          {progressData.percent > 0 && progressData.percent < 100 && (
            <p className="text-xs text-gray-500 mt-1.5">
              {progressData.remaining} warning{progressData.remaining !== 1 ? 's' : ''} remaining
            </p>
          )}
        </div>
      )}

      {/* Parse errors banner */}
      {validation.parseErrors > 0 && (
        <div className="rounded-lg p-4 mb-6 border bg-amber-50 border-amber-200">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-800">
                {validation.parseErrors} row{validation.parseErrors !== 1 ? 's' : ''} failed to parse
              </p>
              <p className="text-sm text-amber-700 mt-0.5">
                Some rows in the file could not be parsed (wrong field count, encoding issues, or malformed data).
                These rows were skipped and are not included in the edit results below.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Truncation warning */}
      {summary.totalIssues > 5000 && (
        <div className="rounded-lg p-4 mb-6 border bg-blue-50 border-blue-200">
          <div className="flex items-start gap-3">
            <InformationCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-blue-800">
                This file has {summary.totalIssues.toLocaleString()} total issues. The detailed edit table shows the first 5,000.
                Use the CSV export for the full set.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* AI Explanations section */}
      {summary.totalIssues > 0 && (
        <div className="mb-8">
          {!explanationsData ? (
            <button
              onClick={handleExplain}
              disabled={explanationsLoading}
              className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-teal-700 transition-colors disabled:opacity-50"
            >
              {explanationsLoading ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  Generating explanations...
                </>
              ) : (
                <>
                  <SparklesIcon className="h-4 w-4" />
                  Generate Explanations
                </>
              )}
            </button>
          ) : (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-5">
              <div className="flex items-center gap-2 mb-3">
                <SparklesIcon className="h-5 w-5 text-purple-600" />
                <h3 className="text-sm font-semibold text-purple-900">Findings Summary</h3>
              </div>
              <p className="text-sm text-purple-800">{explanationsData.summary}</p>

              {explanationsData.explanations && Object.keys(explanationsData.explanations).length > 0 && (
                <div className="mt-4 space-y-3">
                  {Object.entries(explanationsData.explanations).map(([code, exp]: [string, any]) => (
                    <div key={code} className="bg-white rounded-md p-3 border border-purple-100">
                      <p className="text-sm font-semibold text-gray-900">{code}</p>
                      <p className="text-sm text-gray-700 mt-1">{exp.plainEnglish}</p>
                      {exp.suggestedFix && (
                        <p className="text-xs text-purple-700 mt-1">
                          <span className="font-medium">Fix:</span> {exp.suggestedFix}
                        </p>
                      )}
                      {exp.whereToLook && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          <span className="font-medium">Where to look:</span> {exp.whereToLook}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Tab navigation */}
      <div className="border-b border-gray-200 mb-0">
        <nav className="flex gap-6" aria-label="Edit views">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setExpandedLoan(null); setExpandedCode(null); }}
              className={clsx(
                'pb-3 text-sm font-medium border-b-2 transition-colors',
                activeTab === tab.id
                  ? 'border-ocean-600 text-ocean-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              )}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className={clsx(
                  'ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                  activeTab === tab.id ? 'bg-ocean-100 text-ocean-700' : 'bg-gray-100 text-gray-600'
                )}>
                  {tab.count.toLocaleString()}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* ================================================================ */}
      {/* TAB: By Severity (original flat table with filters) */}
      {/* ================================================================ */}
      {activeTab === 'severity' && (
        <div className="bg-white rounded-b-lg border border-t-0 border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Edit Results</h2>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={clsx(
                'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
                showFilters
                  ? 'border-ocean-300 bg-ocean-50 text-ocean-700'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              )}
            >
              <FunnelIcon className="h-4 w-4" />
              Filter
            </button>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex flex-wrap gap-3">
              <select
                value={filter.editType || ''}
                onChange={(e) => {
                  setFilter(f => ({ ...f, editType: e.target.value || undefined }));
                  setEditPage(1);
                }}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-ocean-500 focus:outline-none"
              >
                <option value="">All Types</option>
                <option value="syntactical">Syntactical</option>
                <option value="validity">Validity</option>
                <option value="quality">Quality</option>
                <option value="macro">Macro</option>
                <option value="clarid">Clarid</option>
              </select>
              <select
                value={filter.severity || ''}
                onChange={(e) => {
                  setFilter(f => ({ ...f, severity: e.target.value || undefined }));
                  setEditPage(1);
                }}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-ocean-500 focus:outline-none"
              >
                <option value="">All Severities</option>
                <option value="error">Error</option>
                <option value="warning">Warning</option>
              </select>
              <input
                type="text"
                placeholder="Edit code (e.g. V600)"
                value={editCodeInput}
                onChange={(e) => setEditCodeInput(e.target.value)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-ocean-500 focus:outline-none w-40"
              />
              {(filter.editType || filter.severity || filter.editCode) && (
                <button
                  onClick={() => { setFilter({}); setEditCodeInput(''); setEditPage(1); }}
                  className="text-sm text-ocean-600 hover:text-ocean-700 font-medium"
                >
                  Clear filters
                </button>
              )}
            </div>
          )}

          {editsLoading ? (
            <div className="px-6 py-8 text-center text-gray-500">Loading edits...</div>
          ) : edits.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              {summary.totalIssues === 0
                ? 'No edit issues found. This file is clean!'
                : 'No edits match the current filters.'}
            </div>
          ) : (
            <>
              <EditTable
                edits={edits}
                sort={sort}
                onSort={onSort}
                verifyingEditId={verifyingEditId}
                verifyNote={verifyNote}
                verifyMutation={verifyMutation}
                onVerifyStart={(editId) => setVerifyingEditId(editId)}
                onVerifyCancel={() => { setVerifyingEditId(null); setVerifyNote(''); }}
                onVerifyNoteChange={setVerifyNote}
              />
              <Pagination
                page={editPage}
                totalPages={editTotalPages}
                total={editPagination?.total}
                onPageChange={setEditPage}
                label="edits"
              />
            </>
          )}
        </div>
      )}

      {/* ================================================================ */}
      {/* TAB: By Loan (accordion grouped by row number) */}
      {/* ================================================================ */}
      {activeTab === 'by-loan' && (
        <div className="bg-white rounded-b-lg border border-t-0 border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Issues by Loan</h2>
            <p className="text-sm text-gray-500 mt-0.5">Click a loan row to see all issues for that loan.</p>
          </div>

          {byLoanLoading ? (
            <div className="px-6 py-8 text-center text-gray-500">Loading loan groupings...</div>
          ) : !byLoanData?.loans?.length ? (
            <div className="px-6 py-8 text-center text-gray-500">No loan-level issues found.</div>
          ) : (
            <div className="divide-y divide-gray-200">
              {byLoanData.loans.map((loan: any) => (
                <div key={loan.rowNumber}>
                  {/* Accordion header */}
                  <button
                    onClick={() => setExpandedLoan(expandedLoan === loan.rowNumber ? null : loan.rowNumber)}
                    className="w-full px-6 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-4">
                      {expandedLoan === loan.rowNumber ? (
                        <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                      )}
                      <span className="text-sm font-medium text-gray-900">
                        Row {loan.rowNumber}
                      </span>
                      <div className="flex items-center gap-2">
                        {loan.errors > 0 && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                            <XCircleIcon className="h-3 w-3" />
                            {loan.errors} error{loan.errors !== 1 ? 's' : ''}
                          </span>
                        )}
                        {loan.warnings > 0 && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                            <ExclamationTriangleIcon className="h-3 w-3" />
                            {loan.warnings} warning{loan.warnings !== 1 ? 's' : ''}
                          </span>
                        )}
                        {loan.verified > 0 && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                            <CheckCircleIcon className="h-3 w-3" />
                            {loan.verified} verified
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      {loan.editCodes?.join(', ')}
                    </div>
                  </button>

                  {/* Expanded drill-down */}
                  {expandedLoan === loan.rowNumber && (
                    <div className="bg-gray-50 border-t border-gray-100">
                      {loanDrillLoading ? (
                        <div className="px-6 py-4 text-center text-sm text-gray-500">Loading details...</div>
                      ) : (
                        <EditTable
                          edits={loanDrillData?.edits || []}
                          sort={sort}
                          onSort={onSort}
                          compact
                          verifyingEditId={verifyingEditId}
                          verifyNote={verifyNote}
                          verifyMutation={verifyMutation}
                          onVerifyStart={(editId) => setVerifyingEditId(editId)}
                          onVerifyCancel={() => { setVerifyingEditId(null); setVerifyNote(''); }}
                          onVerifyNoteChange={setVerifyNote}
                        />
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ================================================================ */}
      {/* TAB: By Edit Code (accordion grouped by edit code) */}
      {/* ================================================================ */}
      {activeTab === 'by-code' && (
        <div className="bg-white rounded-b-lg border border-t-0 border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Issues by Edit Code</h2>
            <p className="text-sm text-gray-500 mt-0.5">Systemic issues grouped by CFPB edit check. Click to see affected rows.</p>
          </div>

          {byCodeLoading ? (
            <div className="px-6 py-8 text-center text-gray-500">Loading edit code groupings...</div>
          ) : !byCodeData?.editCodes?.length ? (
            <div className="px-6 py-8 text-center text-gray-500">No edit code groupings found.</div>
          ) : (
            <div className="divide-y divide-gray-200">
              {byCodeData.editCodes.map((group: any) => (
                <div key={group.editCode}>
                  {/* Accordion header */}
                  <button
                    onClick={() => setExpandedCode(expandedCode === group.editCode ? null : group.editCode)}
                    className="w-full px-6 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-4">
                      {expandedCode === group.editCode ? (
                        <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                      )}
                      <span className="text-sm font-mono font-semibold text-gray-900">
                        {group.editCode}
                      </span>
                      <span className={clsx(
                        'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                        group.editType === 'syntactical' && 'bg-red-100 text-red-700',
                        group.editType === 'validity' && 'bg-orange-100 text-orange-700',
                        group.editType === 'quality' && 'bg-amber-100 text-amber-700',
                        group.editType === 'macro' && 'bg-blue-100 text-blue-700',
                        group.editType === 'clarid' && 'bg-teal-100 text-teal-700',
                      )}>
                        {group.editType}
                      </span>
                      <span className={clsx(
                        'text-xs font-medium',
                        group.severity === 'error' ? 'text-red-600' : 'text-amber-600'
                      )}>
                        {group.severity}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-gray-700">
                        {group.count} occurrence{group.count !== 1 ? 's' : ''}
                      </span>
                      {group.verified > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs text-green-600">
                          <CheckCircleIcon className="h-3 w-3" />
                          {group.verified}
                        </span>
                      )}
                    </div>
                  </button>

                  {/* Expanded drill-down */}
                  {expandedCode === group.editCode && (
                    <div className="bg-gray-50 border-t border-gray-100">
                      {group.description && (
                        <div className="px-6 py-2 border-b border-gray-100">
                          <p className="text-xs text-gray-600">{group.description}</p>
                        </div>
                      )}
                      {codeDrillLoading ? (
                        <div className="px-6 py-4 text-center text-sm text-gray-500">Loading details...</div>
                      ) : (
                        <EditTable
                          edits={codeDrillData?.edits || []}
                          sort={sort}
                          onSort={onSort}
                          compact
                          verifyingEditId={verifyingEditId}
                          verifyNote={verifyNote}
                          verifyMutation={verifyMutation}
                          onVerifyStart={(editId) => setVerifyingEditId(editId)}
                          onVerifyCancel={() => { setVerifyingEditId(null); setVerifyNote(''); }}
                          onVerifyNoteChange={setVerifyNote}
                        />
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ====================================================================== */
/* Shared components                                                       */
/* ====================================================================== */

/** A sortable column header. The sort itself is done by the server. */
function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
  cellPad,
}: {
  label: string;
  sortKey: EditSortKey;
  sort?: { sortBy: EditSortKey; sortDir: 'asc' | 'desc' };
  onSort?: (key: EditSortKey) => void;
  cellPad: string;
}) {
  const active = sort?.sortBy === sortKey;
  const base = `${cellPad} text-left text-xs font-medium uppercase`;
  if (!onSort) return <th className={`${base} text-gray-500`}>{label}</th>;
  return (
    <th
      className={`${base} cursor-pointer select-none hover:text-gray-900 ${active ? 'text-gray-900' : 'text-gray-500'}`}
      onClick={() => onSort(sortKey)}
      aria-sort={active ? (sort!.sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
      title={`Sort by ${label}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className={active ? 'text-gray-700' : 'text-gray-300'}>
          {active ? (sort!.sortDir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </span>
    </th>
  );
}

function EditTable({
  edits,
  compact = false,
  verifyingEditId,
  verifyNote,
  verifyMutation,
  onVerifyStart,
  onVerifyCancel,
  onVerifyNoteChange,
  sort,
  onSort,
}: {
  sort?: { sortBy: EditSortKey; sortDir: 'asc' | 'desc' };
  onSort?: (key: EditSortKey) => void;
  edits: any[];
  compact?: boolean;
  verifyingEditId: string | null;
  verifyNote: string;
  verifyMutation: any;
  onVerifyStart: (id: string) => void;
  onVerifyCancel: () => void;
  onVerifyNoteChange: (note: string) => void;
}) {
  if (edits.length === 0) return null;

  const cellPad = compact ? 'px-3 py-2' : 'px-4 py-3';

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <SortHeader label="Edit" sortKey="editCode" sort={sort} onSort={onSort} cellPad={cellPad} />
            <SortHeader label="Type" sortKey="editType" sort={sort} onSort={onSort} cellPad={cellPad} />
            <SortHeader label="Severity" sortKey="severity" sort={sort} onSort={onSort} cellPad={cellPad} />
            <SortHeader label="Row" sortKey="rowNumber" sort={sort} onSort={onSort} cellPad={cellPad} />
            {/* Michael Wallace, 2026-10-06: the data team fixes records by loan
                number, not by the row they happened to land on in an upload. */}
            <SortHeader label="Loan # (ULI)" sortKey="uli" sort={sort} onSort={onSort} cellPad={cellPad} />
            <SortHeader label="Field" sortKey="fieldName" sort={sort} onSort={onSort} cellPad={cellPad} />
            <SortHeader label="Value" sortKey="currentValue" sort={sort} onSort={onSort} cellPad={cellPad} />
            <SortHeader label="Description" sortKey="description" sort={sort} onSort={onSort} cellPad={cellPad} />
            <SortHeader label="Action" sortKey="action" sort={sort} onSort={onSort} cellPad={cellPad} />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {edits.map((edit: any) => (
            <tr key={edit.id} className="hover:bg-gray-50">
              <td className={`${cellPad} text-sm font-mono font-medium text-gray-900`}>
                {edit.editCode}
              </td>
              <td className={cellPad}>
                <span className={clsx(
                  'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                  edit.editType === 'syntactical' && 'bg-red-100 text-red-700',
                  edit.editType === 'validity' && 'bg-orange-100 text-orange-700',
                  edit.editType === 'quality' && 'bg-amber-100 text-amber-700',
                  edit.editType === 'macro' && 'bg-blue-100 text-blue-700',
                  edit.editType === 'clarid' && 'bg-teal-100 text-teal-700',
                )}>
                  {edit.editType}
                </span>
              </td>
              <td className={cellPad}>
                {edit.severity === 'error' ? (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700">
                    <XCircleIcon className="h-3.5 w-3.5" />
                    Error
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700">
                    <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                    Warning
                  </span>
                )}
              </td>
              <td className={`${cellPad} text-sm text-gray-600`}>
                {edit.rowNumber || 'File'}
              </td>
              <td className={`${cellPad} text-xs font-mono text-gray-700 max-w-[180px] truncate`} title={edit.uli || undefined}>
                {edit.uli || <span className="text-gray-300">—</span>}
              </td>
              <td className={cellPad}>
                <div className="text-sm text-gray-900">{edit.fieldName}</div>
                <div className="text-xs text-gray-400">#{edit.fieldNumber}</div>
              </td>
              <td className={`${cellPad} text-sm font-mono text-gray-700 max-w-[120px] truncate`}>
                {edit.currentValue}
              </td>
              <td className={cellPad}>
                <p className={`text-sm text-gray-700 ${compact ? 'max-w-[250px]' : 'max-w-[300px]'}`}>
                  {edit.plainEnglish || edit.description}
                </p>
                {edit.suggestedFix && (
                  <p className="text-xs text-ocean-600 mt-0.5">Fix: {edit.suggestedFix}</p>
                )}
              </td>
              <td className={cellPad}>
                {edit.severity === 'warning' && !edit.verified && (
                  verifyingEditId === edit.id ? (
                    <div className="flex flex-col gap-1">
                      <input
                        type="text"
                        placeholder="Note (optional)"
                        value={verifyNote}
                        onChange={(e) => onVerifyNoteChange(e.target.value)}
                        className="rounded border border-gray-300 px-2 py-1 text-xs w-32 focus:border-ocean-500 focus:outline-none"
                      />
                      <div className="flex gap-1">
                        <button
                          onClick={() => verifyMutation.mutate({ editId: edit.id, note: verifyNote || undefined })}
                          disabled={verifyMutation.isPending}
                          className="text-xs bg-green-600 text-white px-2 py-0.5 rounded hover:bg-green-700 disabled:opacity-50"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={onVerifyCancel}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => onVerifyStart(edit.id)}
                      className="inline-flex items-center gap-1 text-xs text-ocean-600 hover:text-ocean-700 font-medium"
                    >
                      <ShieldCheckIcon className="h-3.5 w-3.5" />
                      Verify
                    </button>
                  )
                )}
                {edit.verified && (
                  <span className="inline-flex items-center gap-1 text-xs text-green-600 font-medium">
                    <CheckCircleIcon className="h-3.5 w-3.5" />
                    Verified
                  </span>
                )}
                {/* Errors used to render an empty cell, which read as the column
                    being broken. It was by design — a syntactical or validity error
                    cannot be verified away, the CFPB will reject the file until the
                    record itself is corrected — but an empty cell says nothing. */}
                {edit.severity === 'error' && (
                  <span
                    className="text-xs text-gray-500"
                    title="Errors cannot be verified away — correct the record in the source file and re-upload."
                  >
                    Fix in file
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Pagination({
  page,
  totalPages,
  total,
  onPageChange,
  label = 'items',
}: {
  page: number;
  totalPages: number;
  total?: number;
  onPageChange: (page: number) => void;
  label?: string;
}) {
  if (totalPages <= 1) return null;

  return (
    <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
      <p className="text-sm text-gray-700">
        Page {page} of {totalPages}
        {total && ` (${total} ${label})`}
      </p>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page <= 1}
          className="p-2 rounded-md border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </button>
        <button
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          className="p-2 rounded-md border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function StatCard({ label, value, color, icon: Icon }: {
  label: string;
  value: number;
  color: 'red' | 'amber' | 'green';
  icon: React.ForwardRefExoticComponent<React.SVGProps<SVGSVGElement> & { title?: string; titleId?: string }>;
}) {
  const colors = {
    red: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: 'text-red-500' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: 'text-amber-500' },
    green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', icon: 'text-green-500' },
  };
  const c = colors[color];

  return (
    <div className={`rounded-lg border p-4 ${c.bg} ${c.border}`}>
      <div className="flex items-center gap-2">
        <Icon className={`h-5 w-5 ${c.icon}`} />
        <p className="text-xs font-medium text-gray-600 uppercase">{label}</p>
      </div>
      <p className={`text-2xl font-bold mt-1 ${c.text}`}>{value}</p>
    </div>
  );
}
